import { useEffect, useState } from "react";
import { X, MessageSquare, CheckCircle2 } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  workspaceId?: string;
  currentScreen?: string;
  title?: string;
  description?: string;
  initialMessage?: string;
  submitLabel?: string;
}

export function ContactSupportModal({
  open,
  onClose,
  workspaceId,
  currentScreen,
  title = "Contact Support",
  description,
  initialMessage = "",
  submitLabel = "Send",
}: Props) {
  const [message, setMessage] = useState("");
  const [hitError, setHitError] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMessage(initialMessage);
    setHitError(false);
    setStatus("idle");
    setErrorMsg(null);
  }, [open, initialMessage]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "sending") return;
    const trimmed = message.trim();
    if (!trimmed) {
      setErrorMsg("Please tell us how we can help.");
      return;
    }
    if (trimmed.length > 4000) {
      setErrorMsg("Message must be 4000 characters or fewer.");
      return;
    }
    setStatus("sending");
    setErrorMsg(null);
    try {
      const res = await fetch("/api/support/request", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          hit_error: hitError,
          workspace_id: workspaceId,
          current_screen: currentScreen,
        }),
      });
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        setStatus("error");
        setErrorMsg(data.message || "Too many requests. Please wait an hour and try again.");
        return;
      }
      if (!res.ok) {
        setStatus("error");
        setErrorMsg("Something went wrong. Please try again, or email founders@sloelabs.com directly.");
        return;
      }
      setStatus("sent");
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  }

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-zinc-500" />
            <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {status === "sent" ? (
          <div className="p-6 text-center">
            <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-3" />
            <p className="text-sm font-medium text-zinc-900 mb-1">Thanks — we got your message.</p>
            <p className="text-xs text-zinc-500">We'll get back to you within 24 hours by email.</p>
            <button onClick={onClose} className="mt-5 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
            {description && (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs leading-relaxed text-zinc-600">
                {description}
              </div>
            )}
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-zinc-700">How can we help?</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe what's happening or what you need…"
                rows={6}
                maxLength={4000}
                className="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none"
                disabled={status === "sending"}
              />
              <span className="text-[11px] text-zinc-400 self-end">{message.length} / 4000</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={hitError}
                onChange={(e) => setHitError(e.target.checked)}
                disabled={status === "sending"}
                className="w-4 h-4"
              />
              <span className="text-xs text-zinc-700">I just hit an error.</span>
            </label>

            {errorMsg && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                {errorMsg}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} disabled={status === "sending"} className="px-4 py-2 rounded-md text-sm text-zinc-700 hover:bg-zinc-100">
                Cancel
              </button>
              <button type="submit" disabled={status === "sending" || !message.trim()} className="px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed">
                {status === "sending" ? "Sending…" : submitLabel}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
